import * as THREE from 'three';
import { Sky } from 'three/addons/objects/Sky.js';

const SKY_SCALE = 450000;
const CLOUD_COUNT = 4;

export type StylizedSky = Sky & {
  userData: Sky['userData'] & {
    baseSunDir: THREE.Vector3;
    clouds?: THREE.Group;
  };
};

export type NightSkyElements = {
  group: THREE.Group;
  stars: THREE.Points;
  moon: THREE.Mesh;
  starMat: THREE.PointsMaterial;
  moonMat: THREE.MeshBasicMaterial;
};

const NIGHT_SKY_RADIUS = 360;
const STAR_COUNT = 1400;

function createSoftCloudTexture(): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(size, size);
  const data = img.data;

  for (let i = 0; i < size * size; i++) {
    const n =
      Math.sin(i * 0.031) * 0.22 +
      Math.sin(i * 0.017 + 1.7) * 0.18 +
      Math.sin(i * 0.009 + 3.1) * 0.14 +
      (Math.random() - 0.5) * 0.08;
    const v = Math.floor((0.52 + n) * 255);
    const idx = i * 4;
    data[idx] = v;
    data[idx + 1] = v;
    data[idx + 2] = Math.min(255, v + 8);
    data[idx + 3] = Math.floor(Math.min(255, Math.max(0, (n + 0.35) * 420)));
  }
  ctx.putImageData(img, 0, 0);

  const blur = document.createElement('canvas');
  blur.width = size;
  blur.height = size;
  const bctx = blur.getContext('2d')!;
  bctx.filter = 'blur(6px)';
  bctx.drawImage(canvas, 0, 0);
  bctx.filter = 'blur(10px)';
  bctx.globalAlpha = 0.55;
  bctx.drawImage(canvas, 0, 0);

  const tex = new THREE.CanvasTexture(blur);
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.needsUpdate = true;
  return tex;
}

function createCloudLayer(scene: THREE.Scene): THREE.Group {
  const group = new THREE.Group();
  const cloudTex = createSoftCloudTexture();
  const cloudMat = new THREE.MeshBasicMaterial({
    map: cloudTex,
    transparent: true,
    opacity: 0.38,
    depthWrite: false,
    fog: false,
    side: THREE.DoubleSide,
  });

  const placements = [
    { x: -120, y: 88, z: -60, sx: 140, sy: 52, rot: 0.12 },
    { x: 95, y: 96, z: 40, sx: 118, sy: 44, rot: -0.28 },
    { x: -30, y: 102, z: 110, sx: 156, sy: 58, rot: 0.45 },
    { x: 70, y: 92, z: -95, sx: 132, sy: 48, rot: -0.08 },
  ];

  for (let i = 0; i < CLOUD_COUNT; i++) {
    const p = placements[i % placements.length];
    const plane = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), cloudMat.clone());
    plane.position.set(p.x + (Math.random() - 0.5) * 18, p.y, p.z + (Math.random() - 0.5) * 18);
    plane.scale.set(p.sx, p.sy, 1);
    plane.rotation.y = p.rot + (Math.random() - 0.5) * 0.35;
    plane.renderOrder = -1;
    const mat = plane.material as THREE.MeshBasicMaterial;
    mat.opacity = 0.28 + Math.random() * 0.16;
    group.add(plane);
  }

  group.userData.cloudTex = cloudTex;
  scene.add(group);
  return group;
}

/** Bright stylized day sky (Preetham); pairs with directional sun direction. */
export function createStylizedSky(scene: THREE.Scene, sunPosition: THREE.Vector3, withClouds = true): StylizedSky {
  const sky = new Sky() as StylizedSky;
  sky.scale.setScalar(SKY_SCALE);
  scene.add(sky);

  const u = sky.material.uniforms;
  u['turbidity'].value = 2.45;
  u['rayleigh'].value = 2.35;
  u['mieCoefficient'].value = 0.0032;
  u['mieDirectionalG'].value = 0.84;

  const baseSunDir = sunPosition.clone().normalize();
  sky.userData.baseSunDir = baseSunDir;
  u['sunPosition'].value.copy(baseSunDir).multiplyScalar(400000);

  if (withClouds) {
    sky.userData.clouds = createCloudLayer(scene);
  }

  scene.background = null;
  return sky;
}

/** Warmer, hazier sky as storm phases advance (0 = phase 1, 1 = phase 6). */
export function applyStormSkyWarmth(sky: StylizedSky, warmth01: number): void {
  const t = Math.max(0, Math.min(1, warmth01));
  const u = sky.material.uniforms;
  u['turbidity'].value += t * 3.6;
  u['rayleigh'].value += t * 2.4;
  u['mieCoefficient'].value += t * 0.0016;
  u['mieDirectionalG'].value = Math.min(0.92, 0.84 + t * 0.06);
}

/** Brief sky brighten/turbidity spike when storm wall lightning strikes on phase advance. */
export function applyStormLightningFlash(sky: StylizedSky, flash01: number): void {
  const t = Math.max(0, Math.min(1, flash01));
  if (t <= 0.001) return;
  const u = sky.material.uniforms;
  u['turbidity'].value += t * 2.4;
  u['rayleigh'].value += t * 5.2;
  u['mieCoefficient'].value += t * 0.0048;
  u['mieDirectionalG'].value = Math.min(0.96, u['mieDirectionalG'].value + t * 0.1);
}

/** Star field + stylized moon mesh for night cycle. */
export function createNightSkyElements(scene: THREE.Scene): NightSkyElements {
  const group = new THREE.Group();
  group.name = 'nightSky';

  const positions = new Float32Array(STAR_COUNT * 3);
  for (let i = 0; i < STAR_COUNT; i++) {
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.random() * Math.PI * 0.48;
    const r = NIGHT_SKY_RADIUS * (0.92 + Math.random() * 0.08);
    const sinPhi = Math.sin(phi);
    positions[i * 3] = Math.cos(theta) * sinPhi * r;
    positions[i * 3 + 1] = Math.cos(phi) * r;
    positions[i * 3 + 2] = Math.sin(theta) * sinPhi * r;
  }

  const starGeo = new THREE.BufferGeometry();
  starGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const starMat = new THREE.PointsMaterial({
    color: 0xe8eeff,
    size: 1.35,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    fog: false,
  });
  const stars = new THREE.Points(starGeo, starMat);
  group.add(stars);

  const moonMat = new THREE.MeshBasicMaterial({
    color: 0xf4f0e6,
    transparent: true,
    opacity: 0,
    fog: false,
    depthWrite: false,
  });
  const moon = new THREE.Mesh(new THREE.SphereGeometry(9, 20, 16), moonMat);
  const moonGlow = new THREE.Mesh(
    new THREE.SphereGeometry(11.5, 16, 12),
    new THREE.MeshBasicMaterial({
      color: 0xc8d8ff,
      transparent: true,
      opacity: 0,
      fog: false,
      depthWrite: false,
    }),
  );
  moon.add(moonGlow);
  group.add(moon);

  group.visible = false;
  scene.add(group);
  return { group, stars, moon, starMat, moonMat };
}

/** Fade stars/moon in at night and track moon opposite the sun. */
export function updateNightSkyElements(
  elements: NightSkyElements,
  nightAmount: number,
  moonDir: THREE.Vector3,
  timeMs: number,
): void {
  const night = Math.max(0, Math.min(1, nightAmount));
  const visible = night > 0.03;
  elements.group.visible = visible;
  if (!visible) return;

  const twinkle = 0.9 + Math.sin(timeMs * 0.0018) * 0.06;
  elements.starMat.opacity = night * 0.82 * twinkle;
  elements.starMat.size = 1.1 + night * 0.35;

  const moonUp = moonDir.y > 0.04;
  elements.moon.visible = moonUp;
  if (moonUp) {
    const dir = moonDir.clone().normalize();
    elements.moon.position.copy(dir.multiplyScalar(NIGHT_SKY_RADIUS * 0.94));
    elements.moonMat.opacity = night * 0.94;
    const glow = elements.moon.children[0];
    if (glow instanceof THREE.Mesh && glow.material instanceof THREE.MeshBasicMaterial) {
      glow.material.opacity = night * 0.22;
    }
  } else {
    elements.moonMat.opacity = 0;
  }

  elements.stars.rotation.y = timeMs * 0.000008;
}

/** Mix POI zone glow into grade-pass mood tint (call from day/night cycle). */
export function applyPoiAmbientGradeTint(
  moodTint: THREE.Color,
  poiTint: THREE.Color,
  blend01: number,
): void {
  const t = Math.max(0, Math.min(1, blend01));
  if (t <= 0.001) return;
  moodTint.lerp(poiTint, t * 0.38);
}

/** Subtle sun drift + cloud parallax for living sky feel. */
export function updateStylizedSky(sky: StylizedSky, time: number): void {
  const base = sky.userData.baseSunDir;
  if (!base) return;

  const sway = Math.sin(time * 0.00018) * 0.012;
  const lift = Math.sin(time * 0.00011 + 1.2) * 0.008;
  sky.material.uniforms['sunPosition'].value.set(
    (base.x + sway) * 400000,
    (base.y + lift) * 400000,
    (base.z + sway * 0.6) * 400000,
  );

  const clouds = sky.userData.clouds;
  if (clouds) {
    clouds.rotation.y = time * 0.000015;
    for (const child of clouds.children) {
      if (!(child instanceof THREE.Mesh)) continue;
      child.position.x += Math.sin(time * 0.00025 + child.id) * 0.002;
      child.position.z += Math.cos(time * 0.00021 + child.id) * 0.0015;
    }
  }
}
